/**
 * "اختر التخصص" — the specialty grid.
 *
 * One tile per specialty, each in its own colour, its icon well lifting and
 * lighting up on hover. The legacy grid painted all twenty-two tiles the same
 * cyan (`renderCategories`, app.js:1572), which made them a wall of identical
 * squares; the colour is what lets you find a specialty by shape rather than by
 * reading every label.
 *
 * The glow is driven by two CSS custom properties the `.category-card` rule in
 * index.css reads, so the hover effect is one stylesheet rule rather than a
 * per-tile inline transition.
 *
 * المرحلة 1.3: the list is the server's (its order, what is hidden), each
 * name in the reader's language, and «مش متأكد؟ ابدأ بطب الأسرة» above the
 * tiles when the scope's bookingJourneys.familyEntry shows it and a
 * family-medicine specialty is visible — by default it is not, and the grid
 * is what it was.
 */

import { Link } from 'react-router-dom';
import { Icon } from '../../components/common';
import { categoryAccent, categoryIcon, categoryLabel } from '../../domain/catalog';
import { useI18n } from '../../i18n/I18nProvider';
import { useBookingPolicy } from '../discovery/parts';

export function CategoryGrid({ categories = [], activeId = null, className = '' }) {
  const policy = useBookingPolicy();
  const family = policy.familyEntry === 'show' ? categories.find((c) => c.isFamilyMedicine) : null;
  const tiles = family ? categories.filter((c) => c.id !== family.id) : categories;

  return (
    <div className="space-y-4">
      {family && <FamilyBanner category={family} />}
      <div
        className={`grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${className}`}
      >
        {tiles.map((category) => (
          <CategoryTile
            key={category.id}
            category={category}
            active={String(activeId) === String(category.id)}
          />
        ))}
      </div>
    </div>
  );
}

/** «مش متأكد من التخصص؟ ابدأ بطب الأسرة» (the plan's screen 4). */
export function FamilyBanner({ category }) {
  const { t, lang } = useI18n();
  const description = (lang === 'en' ? category.descEn : category.descAr) || t('discovery.family.message');
  return (
    <div className="family-banner" data-testid="family-banner">
      <div className="family-banner__text">
        <span className="family-banner__icon">
          <Icon name={category.icon || 'users'} size={22} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-black text-white">{t('discovery.family.title')}</p>
          <p className="text-xs leading-relaxed text-slate-300">{description}</p>
        </div>
      </div>
      <Link to={`/patient/doctors/${category.id}`} className="btn-primary shrink-0">
        <Icon name="stethoscope" size={16} />
        {t('discovery.family.action', { name: categoryLabel(category, lang) })}
      </Link>
    </div>
  );
}

function CategoryTile({ category, active }) {
  const { t, lang } = useI18n();
  const accent = categoryAccent(category.id);
  const count = category.count || 0;

  return (
    <Link
      to={`/patient/doctors/${category.id}`}
      aria-current={active ? 'page' : undefined}
      className="category-card group flex flex-col items-center gap-2 rounded-2xl border p-4 text-center"
      style={{
        // Read by the .category-card hover rule so each tile glows its own hue.
        '--glow': accent.glow,
        '--glow-soft': accent.glowSoft,
        borderColor: active ? accent.glow : 'rgba(255,255,255,0.05)',
        background: active ? accent.tint : 'rgba(255,255,255,0.04)',
      }}
    >
      <span
        className="category-icon flex h-12 w-12 items-center justify-center rounded-xl"
        style={{ background: accent.tint, color: accent.color }}
      >
        <Icon name={category.icon || categoryIcon(category.id)} size={24} />
      </span>

      <span className="text-xs font-bold leading-tight text-white">{categoryLabel(category, lang)}</span>

      {/* "لا يوجد" rather than "0 طبيب": an empty specialty is a state, not a
          count, and reads better than a zero. */}
      <span className="text-[10px] text-slate-500">{count > 0 ? t('discovery.grid.count', { n: count }) : t('discovery.grid.none')}</span>
    </Link>
  );
}

export default CategoryGrid;
